import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { FixedSizeList as List } from "react-window";
import { FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { createPortal } from "react-dom";

export interface CountryOption {
  name: string;
  cities?: Array<{ id: number | string; name: string }>;
  [key: string]: unknown;
}

interface VirtualizedCountrySelectProps {
  countries: CountryOption[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  error?: string;
  withForm?: boolean;
}

const ITEM_HEIGHT = 36;
const VISIBLE_ITEMS = 8;
const LIST_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS;

const VirtualizedCountrySelect: React.FC<VirtualizedCountrySelectProps> = ({
  countries,
  value,
  onValueChange,
  placeholder = "Select Country",
  disabled = false,
  label = "Country",
  error,
  withForm = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<List>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const updateDropdownPosition = () => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      });
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filteredCountries = useMemo(() => {
    if (!searchTerm) return countries;
    return countries.filter((country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [countries, searchTerm]);

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const country = filteredCountries[index];
      if (!country) return null;

      return (
        <div
          style={style}
          className={`flex h-9 cursor-pointer items-center px-3 py-2 text-sm hover:bg-gray-100 ${
            country.name === value ? "bg-gray-100 font-semibold" : ""
          }`}
          onMouseDown={() => {
            onValueChange(country.name);
            setInputValue(country.name);
            setSearchTerm("");
            setIsOpen(false);
          }}
          tabIndex={-1}
        >
          {country.name}
        </div>
      );
    },
    [filteredCountries, onValueChange, value],
  );

  const handleInputFocus = () => {
    if (!disabled) setIsOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && filteredCountries.length > 0) {
      e.preventDefault();
      listRef.current?.scrollToItem(0);
    } else if (e.key === "Enter" && filteredCountries.length > 0) {
      e.preventDefault();
      onValueChange(filteredCountries[0].name);
      setInputValue(filteredCountries[0].name);
      setSearchTerm("");
      setIsOpen(false);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setIsOpen(false), 100);
  };

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [isOpen]);

  const inputField = (
    <div className="relative">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onClick={handleInputFocus}
        onKeyDown={handleInputKeyDown}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {isOpen &&
        createPortal(
          <div
            style={dropdownStyle}
            className="max-h-72 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
          >
            {filteredCountries.length > 0 ? (
              <div
                onWheel={(e) => {
                  e.stopPropagation();
                }}
              >
                <List
                  ref={listRef}
                  height={Math.min(
                    LIST_HEIGHT,
                    filteredCountries.length * ITEM_HEIGHT,
                  )}
                  itemCount={filteredCountries.length}
                  itemSize={ITEM_HEIGHT}
                  width={dropdownStyle.width || "100%"}
                >
                  {Row}
                </List>
              </div>
            ) : (
              <div className="p-3 text-center text-sm text-gray-500">
                {searchTerm ? "No countries found" : "No countries available"}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );

  if (withForm) {
    return (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        {inputField}
        {error && <FormMessage>{error}</FormMessage>}
      </FormItem>
    );
  }

  return inputField;
};

export default VirtualizedCountrySelect;
